import { Fragment, useState } from "react"
import { useSearchParams } from "react-router-dom"
import { Plus, Settings } from "lucide-react"

import {
  LoanProgramsList,
  PageHeader,
  Tabs,
  BrandAutocomplete,
  ProfilesManageTable,
  ManageProfile
} from "../../common/ui"
import { PrimaryBtn } from "../../common/ui/buttons"
import ScenarioPreferencesModal from "../../common/ui/config-admin/config-rate-cards/scenario-preferences-modal"
import {
  useFindDefaultPricingSetsQuery,
  useFindPricingSetsQuery,
  useFindPricingProfilesQuery,
  useFetchAllBrandsQuery,
  useFetchProfilePreferenceMapQuery,
  useUpdateProfilePreferenceMapMutation,
  pricingProfilesApi
} from "../../redux/queries"
import { useAppSelector, useAppDispatch } from "../../common/hooks"
import { useUserRoles } from "../../common/hooks/use-user-roles"
import { selectUserDetails } from "../../redux/slices/auth"
import { RequireRole } from "../../common/validation"
import { ALLOWED_ROLES_BY_ROUTE } from "../../common/constants"

type TabId = "loan-programs" | "profiles"

type Props = {
  title?: string
}

const LoanPrograms: React.FC<Props> = ({ title = "Loan programs" }) => {
  const userDetails = useAppSelector(selectUserDetails)
  const dispatch = useAppDispatch()
  const { isSuperAdmin, user } = useUserRoles()

  const [searchParams] = useSearchParams()
  const [currentTab, setCurrentTab] = useState<TabId>(
    (searchParams.get("tab") as TabId) ?? "loan-programs"
  )
  const [selectedBrandId, setSelectedBrandId] = useState<string>(
    user?.brand_id ?? ""
  )
  const [isCreateOpen, setIsCreateOpen] = useState(false)
  const [showPrefsModal, setShowPrefsModal] = useState(false)

  const effectiveBrandId = isSuperAdmin ? selectedBrandId : user?.brand_id ?? ""

  // Existing loan programs queries
  const { data: pricingSets = [], isLoading: isPricingSetsLoading } =
    useFindPricingSetsQuery(
      { owner: userDetails?.id ?? "" },
      { refetchOnMountOrArgChange: true }
    )

  const { data: defaultPricingSet, isLoading: isDefaultPricingSetLoading } =
    useFindDefaultPricingSetsQuery(
      { loid: userDetails?.id ?? "" },
      { refetchOnMountOrArgChange: true }
    )

  // Profiles tab queries
  const { data: brands = [] } = useFetchAllBrandsQuery(
    {},
    { skip: !isSuperAdmin }
  )

  const { data: profiles = [], isLoading: isProfilesLoading } =
    useFindPricingProfilesQuery(
      { brand_id: effectiveBrandId },
      {
        skip: currentTab !== "profiles" || !effectiveBrandId,
        refetchOnMountOrArgChange: true
      }
    )

  // Profile preferences modal
  const { data: prefMapData, isLoading: isLoadingPrefs } =
    useFetchProfilePreferenceMapQuery(
      { brand_id: effectiveBrandId },
      { skip: !effectiveBrandId || !showPrefsModal }
    )

  const [updatePrefMap, { isLoading: isSavingPrefs }] =
    useUpdateProfilePreferenceMapMutation()

  const handleSavePrefs = async (
    preferences: Record<string, number | null>
  ) => {
    if (!effectiveBrandId) return
    await updatePrefMap({
      profile_preferences: preferences,
      brand_id: Number(effectiveBrandId)
    })
    // Refetch profiles so profile_preference_type updates in the table
    dispatch(pricingProfilesApi.util.invalidateTags(["pricing-profiles"]))
    setShowPrefsModal(false)
  }

  const tabs = [
    { id: "loan-programs" as TabId, label: "Loan Programs" },
    { id: "profiles" as TabId, label: "Profiles" }
  ]

  return (
    <RequireRole allowedRoles={ALLOWED_ROLES_BY_ROUTE["loan-programs"]}>
      <Fragment>
        <PageHeader className="p-6 pb-0 bg-white" title={title}>
          {currentTab === "profiles" && effectiveBrandId && (
            <PrimaryBtn onClick={() => setIsCreateOpen(true)}>
              <Plus size={16} />
              &nbsp;New Profile
            </PrimaryBtn>
          )}
        </PageHeader>

        <div className="px-6 bg-white">
          <Tabs
            tabs={tabs}
            currentTab={currentTab}
            onChange={setCurrentTab}
            urlParam="tab"
          />
        </div>

        {currentTab === "loan-programs" && (
          <LoanProgramsList
            pricingSets={pricingSets}
            isLoading={isPricingSetsLoading || isDefaultPricingSetLoading}
            defaultPricingSetId={defaultPricingSet?.id ?? ""}
          />
        )}

        {currentTab === "profiles" && (
          <div className="bg-white h-full">
            <div className="px-6 pt-4 flex items-center justify-between gap-4">
              {isSuperAdmin ? (
                <BrandAutocomplete
                  brands={brands}
                  selected={selectedBrandId}
                  onChange={setSelectedBrandId}
                />
              ) : (
                <div />
              )}
              {effectiveBrandId && (
                <button
                  type="button"
                  onClick={() => setShowPrefsModal(true)}
                  className="flex items-center gap-1.5 text-sm font-medium text-gray-500 transition-colors hover:text-gray-700 shrink-0"
                >
                  <Settings size={14} />
                  Preferences
                </button>
              )}
            </div>
            {effectiveBrandId ? (
              <div className="px-6">
                <ProfilesManageTable
                  data={profiles}
                  isLoading={isProfilesLoading}
                  brandId={effectiveBrandId}
                />
              </div>
            ) : (
              <div className="px-6 py-12 text-center text-neutral-400">
                Select a brand to view and manage pricing profiles.
              </div>
            )}
          </div>
        )}

        <ManageProfile
          open={isCreateOpen}
          brandId={effectiveBrandId}
          onClose={() => setIsCreateOpen(false)}
        />

        <ScenarioPreferencesModal
          show={showPrefsModal}
          onClose={() => setShowPrefsModal(false)}
          data={prefMapData}
          isLoading={isLoadingPrefs}
          isSaving={isSavingPrefs}
          onSave={handleSavePrefs}
        />
      </Fragment>
    </RequireRole>
  )
}

export default LoanPrograms
